/**
 * PUSH — recording where a phone can be reached.
 *
 * This is the site's half of push notifications. The shell in
 * `shopdesk-app` carries the plugin; this asks it for a token and files the
 * answer in `device_tokens` (migration 0134). Nothing here sends anything.
 *
 * ─── IT ASKS NOBODY ANYTHING UNTIL SOMETHING CAN DELIVER ─────────────
 *
 * Gated on `features.push` from /api/config, which is canSendPush() on the
 * server: PUSH_LIVE=yes AND a credential. Off, this file returns before it
 * touches the plugin at all.
 *
 * The gate is there because registering is not silent. On iOS
 * requestPermissions() shows the system notification prompt, and on
 * 2026-09-20 there was no Firebase project, no APNs key and no push
 * entitlement, so that prompt asked for something that could never arrive.
 * The first person to see it would have been an App Store reviewer, which
 * is the same overclaim the shell's README spent weeks making, pointed at
 * a user instead of a reader.
 *
 * Turning it on is an environment edit on Railway. Not a release of the
 * site, and not a release of the app -- the shell tracks the live site, so
 * both sides pick it up at once.
 */

import { configNow, db } from "./client.ts";
import { hasPush, nativePlatform, requestPushToken } from "./native.ts";

/** Asked once per load. A second boot in the same session is not a new phone. */
let asked = false;

/**
 * Register this phone, if it is a phone, and remember the token.
 *
 * Every failure here is swallowed on purpose. This runs at boot, after the
 * app is already on screen, and there is no version of "we could not store
 * a push token" worth interrupting somebody's morning with. The whole
 * function is a no-op on the web.
 */
export async function registerThisDevice(
  accountId: string, profileId: string,
): Promise<void> {
  if (asked || !hasPush()) return;

  /* Before the plugin, not after. Calling register() and discarding the
     token would still have shown the prompt, which is the whole thing this
     gate exists to prevent. */
  try {
    if (!configNow().features.push) return;
  } catch {
    // configNow throws if boot has not finished. Nothing to do but decline;
    // the next boot asks again.
    return;
  }

  asked = true;

  try {
    const token = await requestPushToken();
    if (!token) return;

    const platform = nativePlatform();
    if (platform === "web") return;

    const sb = await db();
    // No .select(). The insert would run the SELECT policy against the new
    // row and there is nothing here that wants to read it back — see the
    // note in CLAUDE.md about an insert that succeeds and then rolls back
    // on a read policy.
    //
    // onConflict is the token, because FCM and APNs reissue them on
    // reinstall and restore: the same phone comes back with a new token,
    // and the same token never belongs to two phones.
    const { error } = await sb.from("device_tokens").upsert({
      account_id: accountId,
      profile_id: profileId,
      token,
      platform,
      revoked_at: null,
      last_seen_at: new Date().toISOString(),
    }, { onConflict: "token" });

    if (error) {
      // One known and legitimate way to land here: the same phone signing
      // into a DIFFERENT shop. The existing row belongs to the old account,
      // the update policy's using clause is scoped to the current one, and
      // Postgres refuses. Losing the token is the right outcome of the two
      // — the alternative is a policy loose enough to let a session claim
      // any token it can name. It will register again on the next boot,
      // by which time the old row can be revoked by whatever sends.
      console.warn("device token not stored:", error.message);
    }
  } catch (e) {
    console.warn("push registration skipped:", e instanceof Error ? e.message : e);
  }
}
